import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

export default function BroadcasterGroups() {
  const { userId } = useParams<{ userId: string }>()

  // Fetch vendor information
  const { data: vendor, isLoading: vendorLoading } = useQuery({
    queryKey: ['vendor', userId],
    queryFn: async () => {
      if (!userId) return null
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('*')
          .eq('user_id', userId)
          .eq('vendor_type', 'broadcaster')
          .maybeSingle()

        if (error && error.code !== 'PGRST116') {
          console.warn('Error fetching vendor:', error)
          return null
        }
        return data
      } catch (err) {
        console.warn('Error fetching vendor:', err)
        return null
      }
    },
    enabled: !!userId,
  })

  // Fetch groups created by this vendor's user
  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ['broadcaster-groups', userId],
    queryFn: async () => {
      if (!userId) return []
      try {
        const { data, error } = await supabase
          .from('groups')
          .select(`
            id,
            name,
            description,
            created_at,
            group_type
          `)
          .eq('owner_id', userId)
          .eq('group_type', 'broadcaster')
          .order('created_at', { ascending: false })

        if (error) {
          console.warn('Error fetching groups:', error)
          return []
        }
        return (data || [])
      } catch (err) {
        console.warn('Error fetching groups:', err)
        return []
      }
    },
    enabled: !!userId,
  })

  if (vendorLoading || groupsLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <p style={{ color: 'var(--text-primary)' }}>Loading...</p>
      </div>
    )
  }

  if (!vendor) {
    return (
      <div className="max-w-6xl mx-auto">
        <p style={{ color: 'var(--text-primary)' }}>Broadcaster not found.</p>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Back Link */}
      <Link
        to="/broadcasters"
        className="block mb-4"
        style={{ color: 'var(--text-link)' }}
      >
        ← Back to Broadcasters
      </Link>

      {/* Broadcaster Header */}
      <div className="flex items-start gap-6 mb-8">
        {vendor.vendor_logo_url && (
          <div className="flex-shrink-0">
            <img
              src={vendor.vendor_logo_url}
              alt={`${vendor.vendor_name} logo`}
              className="max-h-24 max-w-24 object-contain"
            />
          </div>
        )}
        <div className="flex-1">
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {vendor.vendor_name}
          </h1>
          {vendor.vendor_description && (
            <p 
              className="text-lg mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              {vendor.vendor_description}
            </p>
          )}
          {/* Contact Information */}
          <div className="space-y-1">
            {vendor.vendor_email && (
              <p 
                className="text-sm"
                style={{ color: 'var(--text-primary)' }}
              >
                Contact: {vendor.vendor_email}
              </p>
            )}
            {vendor.vendor_phone && (
              <p 
                className="text-sm"
                style={{ color: 'var(--text-primary)' }}
              >
                Phone: {vendor.vendor_phone}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Groups Section */}
      <div>
        <h2 
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Groups
        </h2>
        {!groups || groups.length === 0 ? (
          <p style={{ color: 'var(--text-primary)' }}>
            No groups available for this broadcaster.
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((group: any) => {
              const createdDate = format(new Date(group.created_at), 'dd/MM/yyyy')
              return (
                <Link
                  key={group.id}
                  to={`/groups/${group.id}`}
                  className="block"
                >
                  <div
                    className="rounded-lg p-6 border-2 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      borderColor: 'var(--bg-tertiary)',
                    }}
                  >
                    <h3 
                      className="text-xl font-bold mb-2 underline"
                      style={{ color: 'var(--text-link)' }}
                    >
                      {group.name}
                    </h3>
                    {group.description && (
                      <p 
                        className="mb-4"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {group.description}
                      </p>
                    )}
                    <p 
                      className="text-sm"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Created: {createdDate}
                    </p>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

